import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../config';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Box,
  Text,
  Button,
  InlineStack,
  Banner,
  ProgressBar,
  Spinner,
  Link,
} from '@shopify/polaris';
import { useBulkJobDetail, useCancelBulkJob } from '../hooks/useBulkJob';
import { PageEntrance, fireConfetti } from '../components/ui';
import './SummaryProgressPage.css';

// ── Rotating status messages ─────────────────────────────────

const SUMMARY_MESSAGES = [
  'Preparing your product catalog…',
  'Analysing product titles and descriptions…',
  'Understanding your brand voice and tone…',
  'Crafting short summaries for AI assistants…',
  'Writing detailed product descriptions…',
  'Generating comparison highlights…',
  'Optimising summaries for ChatGPT discovery…',
  'Tailoring content for Perplexity answers…',
  'Enhancing Google AI Overview compatibility…',
  'Applying category-specific context…',
  'Refining language for conversational AI…',
  'Making your products AI-ready…',
  'Building rich structured content…',
  'Checking quality and accuracy…',
  'Almost done — polishing final summaries…',
] as const;

const MESSAGE_INTERVAL_MS = 8_000;

function useSummaryMessages(isActive: boolean) {
  const [index, setIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setIndex((prev) => Math.min(prev + 1, SUMMARY_MESSAGES.length - 1));
    }, MESSAGE_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive]);

  const message =
    index < SUMMARY_MESSAGES.length
      ? SUMMARY_MESSAGES[index]
      : SUMMARY_MESSAGES.at(-1) ?? '';

  return { message, messageIndex: index, totalMessages: SUMMARY_MESSAGES.length };
}

// ── Completion phases ────────────────────────────────────────

type CompletionPhase = 'filling' | 'celebrating' | 'done' | null;

// ── Page component ───────────────────────────────────────────

export function SummaryProgressPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { data: job, isLoading, isError } = useBulkJobDetail(jobId ?? null);
  const cancelMutation = useCancelBulkJob();

  const isActive = job?.status === 'QUEUED' || job?.status === 'RUNNING';
  const { message } = useSummaryMessages(isActive);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (!jobId) return;
    cancelMutation.mutate(jobId);
  }, [jobId, cancelMutation]);

  // ── Completion animation ─────────────────────────────────
  const [completionPhase, setCompletionPhase] = useState<CompletionPhase>(null);
  const completionStarted = useRef(false);

  useEffect(() => {
    if (job?.status === 'COMPLETED' && !completionStarted.current) {
      completionStarted.current = true;
      setCompletionPhase('filling');

      setTimeout(() => {
        setCompletionPhase('celebrating');
        void fireConfetti('medium');
      }, 1_000);

      setTimeout(() => {
        setCompletionPhase('done');
      }, 3_500);
    }
  }, [job?.status]);

  const isCompleting =
    completionPhase === 'filling' || completionPhase === 'celebrating';
  const isDone = completionPhase === 'done';
  const showCelebration = isCompleting || isDone;

  // ── Progress calculation ─────────────────────────────────
  const computeRawPercent = () => {
    if (!job) return 0;
    if (job.totalProducts <= 0) return 0;
    return Math.round((job.processedCount / job.totalProducts) * 100);
  };
  const rawPercent = computeRawPercent();

  // Cap at 95% until completion; force 100% on COMPLETED
  const computePercent = () => {
    if (job?.status === 'COMPLETED') return 100;
    if (completionPhase === 'filling') return 100;
    return Math.min(rawPercent, 95);
  };
  const percent = computePercent();

  const computeProgressTone = (): 'highlight' | 'success' | 'critical' => {
    if (job?.status === 'COMPLETED' || isCompleting) return 'success';
    if (job?.status === 'FAILED') return 'critical';
    return 'highlight';
  };
  const progressTone = computeProgressTone();

  // ── Loading state ────────────────────────────────────────
  if (isLoading || !job) {
    return (
      <Page title="Generating AI Summaries">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="p" variant="bodyMd">Loading progress…</Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // ── Error fetching status ────────────────────────────────
  if (isError) {
    return (
      <Page title="Generating AI Summaries">
        <Layout>
          <Layout.Section>
            <Banner tone="warning">
              <p>
                Having trouble loading progress. The job is still running in the
                background — try refreshing the page.
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // ── Failed state ─────────────────────────────────────────
  if (job.status === 'FAILED') {
    return (
      <Page title="Summary Generation Failed">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Banner tone="critical">
                  <p>
                    Summary generation failed.
                    {job.failureCount > 0 &&
                      ` ${job.failureCount} of ${job.totalProducts} products encountered errors.`}
                  </p>
                </Banner>

                {job.errorDetails?.length > 0 && (
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingSm">Error details</Text>
                    {job.errorDetails.slice(0, 5).map((err) => (
                      <Text key={err.productId} as="p" variant="bodySm" tone="subdued">
                        {err.productTitle}: {err.error}
                      </Text>
                    ))}
                    {job.errorDetails.length > 5 && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        …and {job.errorDetails.length - 5} more
                      </Text>
                    )}
                  </BlockStack>
                )}

                <Text as="p" variant="bodySm" tone="subdued">Job ID: {jobId}</Text>

                <InlineStack gap="200">
                  <Button variant="primary" onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                  </Button>
                  <Text as="p" variant="bodySm" tone="subdued">
                    If this persists,{' '}
                    <Link
                      url={`mailto:${SUPPORT_EMAIL}?subject=Summary%20job%20failure%20${jobId}`}
                      target="_blank"
                    >
                      contact support
                    </Link>
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // ── Cancelled state ──────────────────────────────────────
  if (job.status === 'CANCELLED') {
    return (
      <Page title="Summary Generation Cancelled">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Generation cancelled</Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {job.successCount} of {job.totalProducts} product summaries were
                  generated before cancellation. You can generate the rest later.
                </Text>
                <InlineStack gap="200">
                  <Button variant="primary" onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // ── Active / completing state ────────────────────────────
  return (
    <PageEntrance>
      <Page
        title={showCelebration ? 'Summaries generated!' : 'Generating AI Summaries'}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                {/* Progress bar */}
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {showCelebration && 'Complete'}
                      {!showCelebration && job.status === 'QUEUED' && 'Queued'}
                      {!showCelebration && job.status !== 'QUEUED' && 'Generating…'}
                    </Text>
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {job.processedCount} / {job.totalProducts} products
                    </Text>
                  </InlineStack>

                  <ProgressBar
                    progress={percent}
                    size="small"
                    tone={progressTone}
                  />

                  <InlineStack align="space-between">
                    <Text as="span" variant="bodySm" tone="subdued">
                      {percent}% complete
                    </Text>
                    {isActive && job.estimatedMinutesRemaining != null && (
                      <Text as="span" variant="bodySm" tone="subdued">
                        ~{job.estimatedMinutesRemaining} min remaining
                      </Text>
                    )}
                  </InlineStack>
                </BlockStack>

                {/* Status message or celebration */}
                {showCelebration ? (
                  <Box paddingBlockStart="400" paddingBlockEnd="400">
                    <BlockStack gap="200" align="center">
                      <Text as="p" variant="headingMd" tone="success" alignment="center">
                        All summaries generated!
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                        Your products are now optimised for AI discovery.
                      </Text>
                    </BlockStack>
                  </Box>
                ) : (
                  <Box paddingBlockStart="200" paddingBlockEnd="200">
                    <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                      {message}
                    </Text>
                  </Box>
                )}

                {/* Success / failure count */}
                {job.processedCount > 0 && !showCelebration && (
                  <InlineStack gap="400">
                    <Text as="span" variant="bodySm" tone="success">
                      {job.successCount} succeeded
                    </Text>
                    {job.failureCount > 0 && (
                      <Text as="span" variant="bodySm" tone="critical">
                        {job.failureCount} failed
                      </Text>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Cancel error */}
          {cancelMutation.isError && (
            <Layout.Section>
              <Banner tone="warning">
                <p>{cancelMutation.error?.message ?? 'Failed to cancel'}</p>
              </Banner>
            </Layout.Section>
          )}

          {/* Actions */}
          {isDone && (
            <Layout.Section>
              <InlineStack align="center">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  Back to dashboard
                </Button>
              </InlineStack>
            </Layout.Section>
          )}
          {isActive && !isCompleting && (
            <Layout.Section>
              <InlineStack align="space-between">
                <Button variant="plain" onClick={() => navigate('/dashboard')}>
                  Go to dashboard
                </Button>
                <Button
                  tone="critical"
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  loading={cancelMutation.isPending}
                >
                  Cancel
                </Button>
              </InlineStack>
            </Layout.Section>
          )}

          {/* Background notice */}
          {isActive && !isCompleting && (
            <Layout.Section>
              <Banner tone="info">
                <p>
                  Generation continues in the background — you can close the app
                  and come back later.
                </p>
              </Banner>
            </Layout.Section>
          )}
        </Layout>
      </Page>
    </PageEntrance>
  );
}
